// Components
import TaskEditor from "../../taskEditor/editor/TaskEditor";
import { ContextualMenu } from "../../../contextualMenu/ContextualMenu";
import { DirectionalHint } from "@fluentui/react";
import { PrimaryButton } from "@fluentui/react";
import { Modal } from "@fluentui/react";
import { Icon } from "@fluentui/react";
import { IconButton } from "../../../iconButton/IconButton";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
// Models
import { TaskKind } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ITask";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { css } from "@fluentui/utilities/lib/css";
import { getRTL } from "@fluentui/utilities/lib/rtl";
// Logging
import { useLoggers } from "../../../../hooks/useLoggers";
// Styles
import { copyIconProps, downIconProps, moveIconProps, linkIconProps, deleteIconProps, dismissIconProps, moreIconProps } from "../../../../icons/icons.styles";
import { completeTaskButtonClassName } from "../../completeTaskAnimationButton/CompleteTaskAnimationButton.styles";
import { getClassNames, getStyles } from "./TaskEditorDialog.styles";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
export var TaskEditorDialogFocusOptions;
(function(TaskEditorDialogFocusOptions) {
    /** User the Default focus element */ TaskEditorDialogFocusOptions[TaskEditorDialogFocusOptions["Default"] = 0] = "Default";
    /** Override focus to the more button */ TaskEditorDialogFocusOptions[TaskEditorDialogFocusOptions["MoreOptionsButton"] = 1] = "MoreOptionsButton";
})(TaskEditorDialogFocusOptions || (TaskEditorDialogFocusOptions = {}));
/**
 * Task editor dialog which contains
 *  - A Dialog which contains the TaskEditor
 *  - Customizations of the FabricDialog chrome
 */ export function TaskEditorDialog({ contentStyles, dialogClassName, dialogContainerClassName, dialogContentClassName, editorProps, fileSuggestionsSeen, focusOverride, focusTrapZoneProps, layerProps, modalProps, onDismiss, theme }) {
    /** Ref to the more options button */ const moreOptionsButtonRef = useRef(null);
    const { engagementLogger } = useLoggers();
    /** Indicates if the "More" context menu is open in the task details dialog */ const [isDialogContextMenuOpen, setIsDialogContextMenuOpen] = useState(false);
    /** Classnames associated with the computed styles  */ const classNames = getClassNames(getStyles(theme, contentStyles));
    /** Effect for focusing on the more options button when focusOverride changes */ useEffect(()=>{
        if (focusOverride === 1) {
            setTimeout(()=>{
                moreOptionsButtonRef.current?.focus();
            }, 0);
        }
    }, [
        focusOverride
    ]);
    /**
     * Used to dismiss the dialog on click
     * @param evt The mouse event that triggered this method
     */ const handleDismiss = useCallback((evt)=>{
        const { task } = editorProps;
        if (task == null) {
            return;
        }
        if (evt) {
            evt.stopPropagation();
        }
        onDismiss(task.id);
    }, [
        editorProps,
        onDismiss
    ]);
    /**
     * Called when click on delete and return corresponding callback based on task recurrence
     * @param task
     */ const handleDelete = (task)=>{
        return task.recurrence?.isActive() ? editorProps.callbacks.openDeleteRecurringTaskDialog : editorProps.callbacks.onDeleteTask;
    };
    /**
     * Called when the "More" context menu is dismissed
     */ const onMoreContextMenuDismiss = useCallback(()=>{
        setIsDialogContextMenuOpen(false);
    }, []);
    /**
     * Called when the task details "More" button is clicked (e.g. the ellipsis)
     * @param event The event that was fired
     */ const onTaskDialogMoreButtonClicked = useCallback((event)=>{
        const { task } = editorProps;
        engagementLogger.logEngagement("TaskEditorDialog", EngagementTelemetryConstants.TaskDetailsContextMenuClicked, {
            taskCreationSourceType: task.taskCreationSourceType ?? ""
        });
        setIsDialogContextMenuOpen((prevState)=>!prevState);
    }, [
        editorProps,
        engagementLogger
    ]);
    /**
     * Callback when the floating file suggestion button is clicked
     * @param evt The mouse event
     */ const onFileSuggestionsFloatingButtonClick = useCallback((evt)=>{
        evt.stopPropagation();
        editorProps.fileSuggestionsContainerRef.current?.scrollIntoView();
    }, [
        editorProps.fileSuggestionsContainerRef
    ]);
    /**
     * Renders the floating suggestions button
     */ const renderFileSuggestionsFloatingButton = ()=>{
        if (!editorProps.config.enableFileSuggestions) {
            return null;
        }
        // File suggestions seen being null indicates the intersection observer hasn't rendered yet. In which case,
        // do not render the button to avoid flickering when rendering with suggestions for the first time
        if (fileSuggestionsSeen == null || fileSuggestionsSeen === true) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(PrimaryButton, {
            className: classNames.suggestionsFloatingButton,
            ariaLabel: Strings.TaskEditor_SuggestionsTemporalButtonText,
            onClick: onFileSuggestionsFloatingButtonClick
        }, /*#__PURE__*/ React.createElement(Icon, downIconProps), /*#__PURE__*/ React.createElement("span", {
            className: "suggestionsButtonText"
        }, Strings.TaskEditor_SuggestionsTemporalButtonText));
    };
    /**
     * Renders the context menu for the dialog
     */ const renderMoreOptionsContextMenu = ()=>{
        const { config, callbacks, task } = editorProps;
        if (!isDialogContextMenuOpen) {
            return null;
        }
        const iconPropsWithDefaultColor = (iconProps)=>({
                ...iconProps,
                className: css("defaultIconColor", iconProps.className)
            });
        const menuItems = [];
        if (config.permissions.canEdit && task.kind !== TaskKind.SharedConsumer) {
            menuItems.push({
                key: "copyTask",
                name: Strings.TaskEditorDialog_CopyTaskText,
                title: Strings.TaskEditorDialog_CopyTaskHelpText,
                iconProps: iconPropsWithDefaultColor(copyIconProps),
                onClick: callbacks.onOpenCopyTaskDialog,
                disabled: !config.enableCopyTask
            });
            menuItems.push({
                key: "moveTask",
                name: Strings.SharedComponents_MoveTaskText,
                title: Strings.SharedComponents_MoveTaskHelpText,
                iconProps: iconPropsWithDefaultColor(moveIconProps),
                onClick: callbacks.openMoveTaskDialog,
                disabled: !config.enableMoveTask
            });
        }
        if (config.enableCopyLinkToTask && !LocalIdGenerator.isLocalTempId(task.id)) {
            menuItems.push({
                key: "copyLinkToTask",
                name: Strings.TaskEditorDialog_CopyLinkToTaskText,
                title: Strings.TaskEditorDialog_CopyLinkToTaskHelpText,
                iconProps: iconPropsWithDefaultColor(linkIconProps),
                onClick: callbacks.onCopyLinkToTask
            });
        }
        if (config.permissions.canEdit && config.permissions.canDelete) {
            menuItems.push({
                key: "deleteTask",
                name: Strings.TaskEditorDialog_DeleteTaskText,
                title: Strings.TaskEditorDialog_DeleteTaskHelpText,
                iconProps: iconPropsWithDefaultColor(deleteIconProps),
                onClick: handleDelete(task)
            });
        }
        return /*#__PURE__*/ React.createElement(ContextualMenu, {
            shouldFocusOnMount: true,
            // For loop client, there can be multiple components, we need to target the menu to the element accordingly
            target: !!layerProps?.hostId ? `#${layerProps?.hostId} .taskEditor-dialog-topButton .moreButton` : ".taskEditor-dialog-topButton .moreButton",
            onDismiss: onMoreContextMenuDismiss,
            directionalHint: getRTL() ? DirectionalHint.bottomLeftEdge : DirectionalHint.bottomRightEdge,
            items: menuItems
        });
    };
    /**
     * Renders the dialog portion of the component
     */ const renderDialog = ()=>{
        const id = "taskEditorDialog";
        const closeButtonAriaLabel = Strings.TaskEditorDialog_CloseButtonAriaLabel;
        return /*#__PURE__*/ React.createElement(Modal, {
            allowTouchBodyScroll: true,
            className: css(classNames.root, "plannerAppDialog", dialogClassName),
            containerClassName: css(classNames.container, dialogContainerClassName),
            isOpen: true,
            layerProps: {
                ...layerProps,
                eventBubblingEnabled: true
            },
            onDismiss: handleDismiss,
            scrollableContentClassName: classNames.scrollable,
            subtitleAriaId: id + "-subText",
            titleAriaId: id + "-title",
            firstFocusableSelector: completeTaskButtonClassName,
            focusTrapZoneProps: focusTrapZoneProps,
            ...modalProps
        }, renderFileSuggestionsFloatingButton(), /*#__PURE__*/ React.createElement("div", {
            className: classNames.header
        }, /*#__PURE__*/ React.createElement("p", {
            className: classNames.title,
            id: id + "-title",
            title: StringHelper.format(Strings.TaskEditorDialog_DialogAriaLabel, editorProps.task.title)
        }), /*#__PURE__*/ React.createElement("div", {
            className: classNames.topButtonContainer
        }, /*#__PURE__*/ React.createElement(IconButton, {
            key: "more",
            className: "moreButton",
            iconProps: moreIconProps,
            ariaLabel: Strings.SharedComponents_MoreButtonTooltipText,
            "aria-expanded": isDialogContextMenuOpen,
            "aria-haspopup": true,
            onClick: onTaskDialogMoreButtonClicked,
            title: Strings.SharedComponents_MoreButtonTooltipText,
            componentRef: moreOptionsButtonRef,
            id: ElementIds.TaskDetailsMoreOptionsButton
        }), renderMoreOptionsContextMenu(), /*#__PURE__*/ React.createElement(IconButton, {
            key: "dismiss",
            className: "ms-Dialog-button ms-Dialog-button--close",
            iconProps: dismissIconProps,
            ariaLabel: closeButtonAriaLabel,
            title: closeButtonAriaLabel,
            onClick: handleDismiss
        }))), /*#__PURE__*/ React.createElement("div", {
            className: css(classNames.content, dialogContentClassName)
        }, /*#__PURE__*/ React.createElement(TaskEditor, editorProps)));
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: "taskEditorDialog"
    }, renderDialog(), editorProps.callbacks.renderDeleteRecurringTaskDialog());
}
